import { ClientDVIR, ClientFleetCompliance, ClientPMSchedule, ClientWorkOrder, InspectionAttachmentSummary } from '@numeo/types-core'
import { ClientTrailer } from '@numeo/types'
import { formatExtraTruckPrice, getTmsTruckSeatLimitError, SeatOverageHandledError } from 'features/billing/tms/overagePrice'
import { ComplianceUnitType } from 'features/fleet/types/types'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useCallback } from 'react'
import {
    bulkCompleteWorkOrders as apiBulkCompleteWorkOrders,
    bulkDeletePMSchedules as apiBulkDeletePMSchedules,
    bulkDeleteWorkOrders as apiBulkDeleteWorkOrders,
    bulkReopenWorkOrders as apiBulkReopenWorkOrders,
    createDVIR as apiCreateDVIR,
    createFleetTruck as apiCreateFleetTruck,
    createPMSchedule as apiCreatePMSchedule,
    createWorkOrder as apiCreateWorkOrder,
    deleteFleetTrailer as apiDeleteFleetTrailer,
    deleteFleetTrucks as apiDeleteFleetTrucks,
    deletePMSchedule as apiDeletePMSchedule,
    deleteWorkOrder as apiDeleteWorkOrder,
    getFleetComplianceAlerts as apiGetFleetComplianceAlerts,
    getTrailerStats as apiGetTrailerStats,
    restoreDVIR as apiRestoreDVIR,
    updateDefectStatus as apiUpdateDefectStatus,
    updateFleetTrailer as apiUpdateFleetTrailer,
    updateFleetTruck as apiUpdateFleetTruck,
    updateTrailerCompliance as apiUpdateTrailerCompliance,
    updateTruckCompliance as apiUpdateTruckCompliance,
    updateWorkOrder as apiUpdateWorkOrder,
    voidDVIR as apiVoidDVIR,
    DVIRFilterParams,
    FleetEquipmentTrailer,
    FleetEquipmentTruck,
    FleetTruckUpdate,
    getDVIRs,
    getFleetCompliance,
    getFleetEquipment,
    getFleetOverview,
    getPMSchedules,
    getWorkOrders,
    WorkOrderFilterParams,
    type BulkActionResult,
} from 'services/fleetApi'
import { createTrailer as apiCreateTrailer } from 'services/trailerApi'
import {
    selectFleetCompliance,
    selectFleetComplianceAlerts,
    selectFleetDVIRs,
    selectFleetEquipment,
    selectFleetOverview,
    selectFleetPMSchedules,
    selectFleetWorkOrders,
    selectTrailerStats,
    setComplianceAlerts,
    setComplianceAlertsError,
    setComplianceAlertsLoading,
    setComplianceError,
    setComplianceLoading,
    setComplianceRecords,
    setDVIRs,
    setDVIRsError,
    setDVIRsLoading,
    updateDVIRAttachments as patchDVIRAttachments,
    setEquipmentData,
    setEquipmentError,
    setEquipmentLoading,
    setOverviewData,
    setOverviewError,
    setOverviewLoading,
    setPMSchedules,
    setPMSchedulesError,
    setPMSchedulesLoading,
    setTrailerStatsData,
    setTrailerStatsError,
    setTrailerStatsLoading,
    setWorkOrders,
    setWorkOrdersError,
    setWorkOrdersLoading,
} from 'store/fleetSlice'
import { showSnackbar, showSuccess, SnackbarType } from 'utils/snackbar'

export const useFleetData = () => {
    const dispatch = useAppDispatch()
    const overview = useAppSelector(selectFleetOverview)
    const equipment = useAppSelector(selectFleetEquipment)
    const compliance = useAppSelector(selectFleetCompliance)
    const complianceAlerts = useAppSelector(selectFleetComplianceAlerts)
    const workOrders = useAppSelector(selectFleetWorkOrders)
    const pmSchedules = useAppSelector(selectFleetPMSchedules)
    const dvirs = useAppSelector(selectFleetDVIRs)
    const trailerStats = useAppSelector(selectTrailerStats)

    const fetchOverview = useCallback(async () => {
        dispatch(setOverviewLoading(true))
        try {
            const data = await getFleetOverview()
            dispatch(setOverviewData(data))
        } catch (error: unknown) {
            dispatch(setOverviewError(error instanceof Error ? error.message : 'Failed to fetch fleet overview'))
        }
    }, [dispatch])

    const fetchEquipment = useCallback(async () => {
        dispatch(setEquipmentLoading(true))
        try {
            const data = await getFleetEquipment()
            dispatch(setEquipmentData(data))
        } catch (error: unknown) {
            dispatch(setEquipmentError(error instanceof Error ? error.message : 'Failed to fetch equipment'))
        }
    }, [dispatch])

    const fetchCompliance = useCallback(async () => {
        dispatch(setComplianceLoading(true))
        try {
            const data = await getFleetCompliance()
            dispatch(setComplianceRecords(data))
        } catch (error: unknown) {
            dispatch(setComplianceError(error instanceof Error ? error.message : 'Failed to fetch compliance'))
        }
    }, [dispatch])

    const fetchComplianceAlerts = useCallback(async () => {
        dispatch(setComplianceAlertsLoading(true))
        try {
            const data = await apiGetFleetComplianceAlerts()
            dispatch(setComplianceAlerts(data))
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Failed to fetch compliance alerts'
            dispatch(setComplianceAlertsError(msg))
        }
    }, [dispatch])

    const fetchWorkOrders = useCallback(
        async (filters?: WorkOrderFilterParams) => {
            dispatch(setWorkOrdersLoading(true))
            try {
                const data = await getWorkOrders(filters)
                dispatch(setWorkOrders(data))
            } catch (error: unknown) {
                dispatch(setWorkOrdersError(error instanceof Error ? error.message : 'Failed to fetch work orders'))
            }
        },
        [dispatch]
    )

    const fetchPMSchedules = useCallback(async () => {
        dispatch(setPMSchedulesLoading(true))
        try {
            const data = await getPMSchedules()
            dispatch(setPMSchedules(data))
        } catch (error: unknown) {
            dispatch(setPMSchedulesError(error instanceof Error ? error.message : 'Failed to fetch PM schedules'))
        }
    }, [dispatch])

    const fetchDVIRs = useCallback(
        async (filters?: DVIRFilterParams, options?: { silent?: boolean }) => {
            if (!options?.silent) dispatch(setDVIRsLoading(true))
            try {
                const data = await getDVIRs(filters)
                dispatch(setDVIRs(data))
            } catch (error: unknown) {
                dispatch(setDVIRsError(error instanceof Error ? error.message : 'Failed to fetch DVIRs'))
            }
        },
        [dispatch]
    )

    const createWorkOrder = useCallback(
        async (data: Partial<ClientWorkOrder>): Promise<ClientWorkOrder> => {
            const result = await apiCreateWorkOrder(data)
            await fetchWorkOrders()
            return result
        },
        [fetchWorkOrders]
    )

    const updateWorkOrder = useCallback(
        async (id: string, data: Partial<ClientWorkOrder>): Promise<ClientWorkOrder> => {
            const result = await apiUpdateWorkOrder(id, data)
            await fetchWorkOrders()
            return result
        },
        [fetchWorkOrders]
    )

    const createDVIR = useCallback(
        async (data: Partial<ClientDVIR>, files: File[] = []) => {
            await apiCreateDVIR(data, files)
            await fetchDVIRs()
        },
        [fetchDVIRs]
    )

    const updateCompliance = useCallback(
        async (unitType: ComplianceUnitType, unitId: string, data: Partial<ClientFleetCompliance>) => {
            if (unitType === ComplianceUnitType.Truck) {
                await apiUpdateTruckCompliance(unitId, data)
            } else {
                await apiUpdateTrailerCompliance(unitId, data)
            }
            await fetchCompliance()
        },
        [fetchCompliance]
    )

    const updateDefectStatus = useCallback(
        async (dvirId: string, defectId: string, data: { status: string; workOrderId?: string }) => {
            await apiUpdateDefectStatus(dvirId, defectId, data)
            await fetchDVIRs()
        },
        [fetchDVIRs]
    )

    const voidDVIR = useCallback(
        async (id: string, reason: string) => {
            await apiVoidDVIR(id, reason)
            await fetchDVIRs()
        },
        [fetchDVIRs]
    )

    const restoreDVIR = useCallback(
        async (id: string) => {
            await apiRestoreDVIR(id)
            await fetchDVIRs()
        },
        [fetchDVIRs]
    )

    const updateDVIRAttachments = useCallback(
        (id: string, attachments: InspectionAttachmentSummary[]) => dispatch(patchDVIRAttachments({ id, attachments })),
        [dispatch]
    )

    const createPMSchedule = useCallback(
        async (data: Partial<ClientPMSchedule>): Promise<ClientPMSchedule> => {
            const result = await apiCreatePMSchedule(data)
            // Refresh errors must not reject the create operation: the schedule and its invoice
            // reference are already persisted, so the caller must not clean up the uploaded file.
            void fetchPMSchedules()
            return result
        },
        [fetchPMSchedules]
    )

    const deleteWorkOrder = useCallback(
        async (id: string): Promise<void> => {
            await apiDeleteWorkOrder(id)
            await fetchWorkOrders()
        },
        [fetchWorkOrders]
    )

    const deletePMSchedule = useCallback(
        async (id: string): Promise<void> => {
            await apiDeletePMSchedule(id)
            await fetchPMSchedules()
        },
        [fetchPMSchedules]
    )

    const createTruck = useCallback(
        async (data: Partial<FleetEquipmentTruck>, acceptOverage?: boolean) => {
            try {
                const result = await apiCreateFleetTruck(data, acceptOverage)
                await fetchEquipment()
                // Active subscriber went past the block → server bumped the truck qty (prorated).
                if ((result as unknown as { billing?: { charged?: boolean } })?.billing?.charged) {
                    showSuccess('Truck added — your subscription was updated')
                }
            } catch (error) {
                const seatLimitError = getTmsTruckSeatLimitError(error)
                // Trial over the block → hard block, push to buy.
                if (seatLimitError?.code === 'TMS_SEAT_LIMIT_TRIAL') {
                    showSnackbar(SnackbarType.WARNING, seatLimitError.message || 'You have reached your included trucks. Upgrade to add more.', 12000, {
                        title: 'See plans',
                        onClick: () => window.location.assign('/billing?tab=tms'),
                    })
                    throw new SeatOverageHandledError()
                }
                // Active over the block → warn, then auto-charge on confirm.
                if (seatLimitError?.code === 'TMS_SEAT_OVERAGE_CONFIRM') {
                    const priceStr = await formatExtraTruckPrice(seatLimitError.data.tier, seatLimitError.data.billingInterval)
                    showSnackbar(
                        SnackbarType.WARNING,
                        `Adding this truck exceeds your ${seatLimitError.data.purchasedTrucks ?? 'included'} trucks. Your bill will increase by ${priceStr}. Add & update billing?`,
                        15000,
                        {
                            title: 'Add & update billing',
                            onClick: () => void createTruck(data, true),
                        }
                    )
                    throw new SeatOverageHandledError()
                }
                throw error
            }
        },
        [fetchEquipment]
    )

    const createTrailer = useCallback(
        async (data: Partial<ClientTrailer>) => {
            await apiCreateTrailer(data)
            await fetchEquipment()
        },
        [fetchEquipment]
    )

    const updateTruck = useCallback(
        async (truckId: string, data: FleetTruckUpdate) => {
            await apiUpdateFleetTruck(truckId, data)
            await fetchEquipment()
        },
        [fetchEquipment]
    )

    const fetchTrailerStats = useCallback(async () => {
        dispatch(setTrailerStatsLoading(true))
        try {
            const data = await apiGetTrailerStats()
            dispatch(setTrailerStatsData(data))
        } catch (error: unknown) {
            dispatch(setTrailerStatsError(error instanceof Error ? error.message : 'Failed to fetch trailer stats'))
        }
    }, [dispatch])

    const updateTrailer = useCallback(
        async (trailerId: string, data: Partial<FleetEquipmentTrailer>) => {
            await apiUpdateFleetTrailer(trailerId, data)
            await fetchEquipment()
            await fetchTrailerStats()
        },
        [fetchEquipment, fetchTrailerStats]
    )

    const deleteTrailer = useCallback(
        async (trailerId: string) => {
            await apiDeleteFleetTrailer(trailerId)
            await fetchEquipment()
            await fetchTrailerStats()
        },
        [fetchEquipment, fetchTrailerStats]
    )

    const deleteTruck = useCallback(
        async (truckId: string) => {
            await apiDeleteFleetTrucks([truckId])
            await fetchEquipment()
        },
        [fetchEquipment]
    )

    const bulkDeleteWorkOrders = useCallback(
        async (ids: string[]): Promise<void> => {
            await apiBulkDeleteWorkOrders(ids)
            await fetchWorkOrders()
        },
        [fetchWorkOrders]
    )

    const bulkCompleteWorkOrders = useCallback(
        async (ids: string[]): Promise<BulkActionResult> => {
            const result = await apiBulkCompleteWorkOrders(ids)
            await fetchWorkOrders()
            return result
        },
        [fetchWorkOrders]
    )

    const bulkReopenWorkOrders = useCallback(
        async (ids: string[]): Promise<BulkActionResult> => {
            const result = await apiBulkReopenWorkOrders(ids)
            await fetchWorkOrders()
            return result
        },
        [fetchWorkOrders]
    )

    const bulkDeletePMSchedules = useCallback(
        async (ids: string[]): Promise<void> => {
            await apiBulkDeletePMSchedules(ids)
            await fetchPMSchedules()
        },
        [fetchPMSchedules]
    )

    return {
        overview,
        equipment,
        compliance,
        workOrders,
        pmSchedules,
        dvirs,
        fetchOverview,
        fetchEquipment,
        fetchCompliance,
        complianceAlerts,
        fetchComplianceAlerts,
        fetchWorkOrders,
        fetchPMSchedules,
        fetchDVIRs,
        createWorkOrder,
        updateWorkOrder,
        createDVIR,
        createPMSchedule,
        updateCompliance,
        updateDefectStatus,
        voidDVIR,
        restoreDVIR,
        updateDVIRAttachments,
        createTruck,
        createTrailer,
        updateTruck,
        trailerStats,
        fetchTrailerStats,
        updateTrailer,
        deleteTrailer,
        deleteTruck,
        deleteWorkOrder,
        bulkDeleteWorkOrders,
        bulkCompleteWorkOrders,
        bulkReopenWorkOrders,
        deletePMSchedule,
        bulkDeletePMSchedules,
    }
}
